<template>
  <div class="table-section">
    <div class="table-top">
      <b-row>
        <b-col md="4" lg="4" class="my-1">
          <p class="table-filter text-left">
            <BaseInput
              type="text"
              label="Search"
              :inputType="'text'"
              v-model="filter"
              placeholder="Search something..."
              :hasPreIcon="false"
            >
            </BaseInput>
          </p>
        </b-col>
        <b-col md="8" lg="8" class="my-1">
          <p class="table-action text-right">
            <span class="excel-download-btn-hold">
              <button
                type="button"
                class="btn btn-primary btn-semi-rounded mb-2 mr-2"
              >
                <i class="bx bxs-report"></i> &nbsp; &nbsp;
                <download-excel
                  class="excel-download-btn"
                  :data="items"
                  :fields="json_fields"
                  worksheet="My Worksheet"
                  name="filename.xls"
                >
                  Download As Excel
                </download-excel>
              </button>
            </span>
          </p>
        </b-col>
      </b-row>
    </div>

    <b-table
      striped
      hover
      :fields="fields"
      :items="items"
      :busy="isBusy"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      sort-icon-left
      responsive="sm"
      :current-page="currentPage"
      :per-page="perPage"
      :filter="filter"
      :filterIncludedFields="filterOn"
      :sort-direction="sortDirection"
      @filtered="onFiltered"
    >
      <!-- A virtual column -->
      <template v-slot:cell(index)="data">
        {{ data.index + 1 }}
      </template>

      <template v-slot:cell(batchName)="data">
        <p class="name-field">
          <span class="view-more-info">
            <a
              @click="viewBatch(data.item)"
              class="view-customer-info-btn"
              v-b-tooltip.hover.right
              title="View Batch Entries"
            >
              <i class="bx bx-info-circle"></i>
            </a>
          </span>
          <span>{{ data.item.batchName }}</span>
        </p>
      </template>
      <template v-slot:cell(batchStatus)="data">
        <p class="name-field">
          <span
            v-if="data.item.batchStatus === 'OPEN'"
            class="badge badge-pill badge-soft-success font-size-12"
            >{{ data.item.batchStatus }}</span
          >
          <span
            v-if="data.item.batchStatus === 'CLOSED'"
            class="badge badge-pill badge-soft-danger font-size-12"
            >{{ data.item.batchStatus }}</span
          >
        </p>
      </template>

      <template v-slot:cell(action)="data">
        <p class="name-field">
          <span
            v-if="data.item.batchStatus === 'CLOSED'"
            class="badge badge-pill badge-soft-danger font-size-12"
            >{{ data.item.batchStatus }}</span
          >
          <span v-if="data.item.batchStatus === 'OPEN'">
            <button
              @click="addEntryToBatch(data.item)"
              class="btn btn-secondary btn-sm mr-1"
              v-b-tooltip.hover.right
              title="Add Entry To Batch"
            >
              <i class="bx bx-add-to-queue"></i>
            </button>
          </span>
          <span v-if="data.item.batchStatus === 'OPEN'">
            <button
              @click="closeBatch(data.item)"
              class="btn btn-danger btn-sm mr-2"
            >
              Close
            </button>
          </span>

          <span v-if="data.item.batchStatus === 'OPEN'">
            <button
              @click="deleteBatch(data.item)"
              class="btn btn-outline-danger btn-sm mr-1"
              v-b-tooltip.hover.right
              title="Delete Entry"
            >
              <i class="bx bx-trash"></i>
            </button>
          </span>
        </p>
      </template>

      <!-- Optional default data cell scoped slot -->
      <template v-slot:cell()="data">
        <i>{{ data.value }}</i>
      </template>
    </b-table>

    <b-row>
      <b-col sm="5" md="4" class="my-1">
        <b-form-group
          label="Per page"
          label-cols-sm="6"
          label-cols-md="4"
          label-cols-lg="3"
          label-align-sm="right"
          label-size="sm"
          label-for="perPageSelect"
          class="mb-0"
        >
          <b-form-select
            v-model="perPage"
            id="perPageSelect"
            size="sm"
            :options="pageOptions"
          ></b-form-select>
        </b-form-group>
      </b-col>

      <b-col sm="7" md="8" class="my-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="fill"
          size="sm"
          class="my-0"
        ></b-pagination>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapState } from "vuex";
import StoreUtils from "../../../../utils/BaseUtils/StoreUtils";
import RouterUtils from "../../../../utils/BaseUtils/RouterUtils";
import NotificationUtils from "../../../../utils/BaseUtils/NotificationUtils";

export default {
  name: "AllTerminalBatchesTable",
  data() {
    return {
      sortBy: "date",
      sortDesc: false,
      totalRows: 1,
      currentPage: 1,
      perPage: 5,
      pageOptions: [5, 10, 15],
      sortDirection: "asc",
      filter: null,
      filterOn: [],
      // Note 'isActive' is left out and will not appear in the rendered table
      fields: [
        // A virtual column that doesn't exist in items
        { key: "index", label: "S/N", sortable: true, class: "text-center" },
        // A column that needs custom formatting
        { key: "batchName", label: "Batch Name", sortable: true },
        { key: "batchReference", label: "Batch Reference", sortable: true },
        { key: "batchStatus", label: "Batch Status", sortable: true },
        { key: "batchCreatedAt", label: "Created At", sortable: true },
        { key: "batchUpdatedAt", label: "Updated At", sortable: true },
        { key: "action", label: "action", sortable: true }
      ],
      json_fields: {
        batchName: "batchName",
        batchReference: "batchReference",
        batchCreatedAt: "batchCreatedAt",
        batchUpdatedAt: "batchUpdatedAt",
        batchStatus: "batchStatus",
        batchBankCode: "batchBankCode",
        batchChecker: "batchChecker",
        batchMaker: "batchMaker",
        batchId: "batchId"
      },
      json_meta: [
        [
          {
            key: "charset",
            value: "utf-8"
          }
        ]
      ]
    };
  },
  methods: {
    // showDeleteConfirmationModal(batch) {
    //     this.currentBatchToDelete = batch;
    //
    //     let notificationModalBody = {
    //         type: "warning", //error, success, warning
    //         mode: "yes-no", // default, yes-no
    //         title: "Confirmation",
    //         message: "Are you sure you want to assign these entries?",
    //         noAction: () => {
    //             NotificationUtils.closeNotificationModal();
    //         },
    //         noActionTitle: "No",
    //         yesAction: () => {
    //             StoreUtils.dispatch(
    //                 StoreUtils.actions.accountingEntry.ASSIGN_ENTRIES,
    //                 this.accountingEntry.currentAccountingEntryEntries
    //             );
    //             NotificationUtils.closeNotificationModal();
    //         },
    //         yesActionTitle: "Yes"
    //     };
    //
    //     NotificationUtils.showNotificationModal(notificationModalBody);
    // },
    // deleteBatch(batch) {
    //   StoreUtils.dispatch(StoreUtils.actions.journal., batch);
    // },
    deleteBatch(batch) {
      let notificationModalBody = {
        type: "warning", //error, success, warning
        mode: "yes-no", // default, yes-no
        title: "Confirmation",
        message: "Are you sure you want to delete this batch?",
        noAction: () => {
          NotificationUtils.closeNotificationModal();
        },
        noActionTitle: "No",
        yesAction: () => {
          StoreUtils.dispatch("terminal/deleteBatch", batch.batchReference);
          NotificationUtils.closeNotificationModal();
        },
        yesActionTitle: "Yes"
      };

      NotificationUtils.showNotificationModal(notificationModalBody);
    },
    closeBatch(batch) {
      StoreUtils.dispatch("terminal/closeBatch", batch);
    },
    addEntryToBatch(batch) {
      StoreUtils.dispatch(StoreUtils.actions.journal.SET_CURRENT_BATCH, batch);
      RouterUtils.changeRouteTo(RouterUtils.routes.terminal.ADD_ENTRY_TO_BATCH);
    },
    viewBatch(batch) {
      StoreUtils.dispatch(StoreUtils.actions.journal.SET_CURRENT_BATCH, batch);
      RouterUtils.changeRouteTo(RouterUtils.routes.terminal.ALL_BATCH_ENTRIES);
    },
    returnItems() {},
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    }
  },

  computed: {
    ...mapState({
      items: state => state.table.tableData,
      isBusy: state => state.loader.tableLoader,
      loader: state => state.loader
    }),
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => {
          return { text: f.label, value: f.key };
        });
    }
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.items.length;
    this.filter = " ";
  },

  created() {
    StoreUtils.dispatch("terminal/fetchAllBatches");
  }
};
</script>

<style scoped>
/*.VueTables__date-filter {*/
/*  border: 1px solid #ccc;*/
/*  padding: 6px;*/
/*  border-radius: 4px;*/
/*  cursor: pointer;*/
/*}*/
.view-more-info i {
  font-weight: 600;
}
.view-more-info {
  font-weight: 600;
  margin-right: 3px;
  cursor: pointer;
}
</style>
